import React, { ReactNode, useState } from 'react';
import {
  DndContext,
  rectIntersection,
  useSensor,
  useSensors,
  MouseSensor,
  DragOverEvent,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';

import { useDeepCompareMemo } from '@clatter/platform';
import { FolderManagerDragEndPayload, FolderManagerDragStartPayload } from '../../../interfaces';
import { DragOverlay } from '../../UI';

//region INTERFACES
interface DraggableDnDContainerProps {
  children: ReactNode;
  items: FolderManagerItem[];
  onDragEnd: (data: FolderManagerDragEndPayload) => void;
  onDragStart?: (data: FolderManagerDragStartPayload) => void;
  disableInternalDnD?: boolean;
}

//endregion

export const DraggableDnDContainer = ({ items, onDragEnd, onDragStart, children, disableInternalDnD = false }: DraggableDnDContainerProps) => {
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10, // activation constraint is needed to enable click events from slide item
      },
    }),
  );

  //region STATE
  const [overId, setOverId] = useState<string | null>(null);
  const [draggedItems, setDraggedItems] = useState<any[] | null>(null);
  //endregion

  //region COMPUTED
  const contextIds = useDeepCompareMemo(
    () =>
      items.map((item) => {
        if (!item.id) {
          console.error('Invalid data provided!');
          return;
        }
        return item.id?.toString();
      }),
    [items],
  );
  //endregion

  //region METHODS
  const handleDragEnd = (data: DragEndEvent) => {
    setOverId(null);
    setDraggedItems(null);

    if (!data?.over?.data?.current?.['type'] || !data?.over?.id) {
      console.log('ERROR: invalid data provided for handleDragEnd');
      return;
    }

    if (typeof onDragEnd === 'function') {
      //@ts-ignore
      onDragEnd({ id: data?.over?.id, type: data?.over?.data?.current?.['type'] });
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    if (!event || !event?.active?.id) {
      console.log('ERROR: missing dragging item id.');
    }

    const draggedItem = event?.active?.data?.current?.['item'];
    if (draggedItem) {
      setDraggedItems([draggedItem]);
    }

    if (!onDragStart || typeof onDragStart !== 'function') {
      console.log('No onDragStart method provided. Skipping.');
      return;
    }

    //@ts-ignore
    onDragStart({ id: event?.active?.id });
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (over?.id !== undefined && active?.id !== over?.id) {
      //@ts-ignore
      setOverId(over.id);
    }
  };
  //endregion

  // when disableInternalDnD is true, just render children without DndContext
  // this allows using an external DndContext for cross-container drag and drop
  if (disableInternalDnD) {
    return (
      <SortableContext items={contextIds}>
        {React.Children.map(children, (child) =>
          // @ts-ignore
          React.isValidElement(child) ? React.cloneElement(child, { overId }) : child,
        )}
      </SortableContext>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={rectIntersection}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
    >
      {/*//@ts-ignore*/}
      <SortableContext items={contextIds}>
        {React.Children.map(children, (child) =>
          // @ts-ignore
          React.isValidElement(child) ? React.cloneElement(child, { overId }) : child,
        )}
      </SortableContext>

      <DragOverlay draggedItems={draggedItems} />
    </DndContext>
  );
};
